/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ExperimentalRecommenderQueryParams } from "../models/ExperimentalRecommenderQueryParams";
import type { RecommendationResult } from "../models/RecommendationResult";
import type { RecommenderQueryParams } from "../models/RecommenderQueryParams";
import type { ScoredRecommenderQueryParams } from "../models/ScoredRecommenderQueryParams";
import type { SpotSearchQueryParams } from "../models/SpotSearchQueryParams";
import type { SpotSearchQueryResult } from "../models/SpotSearchQueryResult";

import type { CancelablePromise } from "../core/CancelablePromise";
import type { BaseHttpRequest } from "../core/BaseHttpRequest";

export class DefaultService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * モジュールのバージョンを返します。
   * @returns any Successful Response
   * @throws ApiError
   */
  public getVersion(): CancelablePromise<any> {
    return this.httpRequest.request({
      method: "GET",
      url: "/version"
    });
  }

  /**
   * 検索結果を返します。
   * @returns SpotSearchQueryResult Successful Response
   * @throws ApiError
   */
  public searchSpots({
    requestBody
  }: {
    requestBody?: SpotSearchQueryParams | null;
  }): CancelablePromise<SpotSearchQueryResult> {
    return this.httpRequest.request({
      method: "POST",
      url: "/search",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`
      }
    });
  }

  /**
   * 推薦結果を返します。
   * @returns RecommendationResult Successful Response
   * @throws ApiError
   */
  public getRecommendation({
    recommendationId,
    requestBody
  }: {
    recommendationId?: string | null;
    requestBody?: RecommenderQueryParams | null;
  }): CancelablePromise<RecommendationResult> {
    return this.httpRequest.request({
      method: "POST",
      url: "/recommend",
      query: {
        recommendation_id: recommendationId
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`
      }
    });
  }

  /**
   * 推薦結果を返します。
   * @returns RecommendationResult Successful Response
   * @throws ApiError
   */
  public getExperimentalRecommendation({
    recommendationId,
    requestBody
  }: {
    recommendationId?: string | null;
    requestBody?: ExperimentalRecommenderQueryParams | null;
  }): CancelablePromise<RecommendationResult> {
    return this.httpRequest.request({
      method: "POST",
      url: "/experimental_recommend",
      query: {
        recommendation_id: recommendationId
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`
      }
    });
  }

  /**
   * 設定管理されたスコア関数を使用して推薦結果を返します。
   * 設定管理されたスコア関数を使用する推薦エンドポイント
   *
   * - function_typeに基づいて、api-config-managerから安全なスコア関数を取得
   * - 取得に失敗した場合はデフォルト関数を使用
   * - セキュリティリスクを排除したエンドポイント
   * @returns RecommendationResult Successful Response
   * @throws ApiError
   */
  public getScoredRecommendation({
    recommendationId,
    requestBody
  }: {
    recommendationId?: string | null;
    requestBody?: ScoredRecommenderQueryParams | null;
  }): CancelablePromise<RecommendationResult> {
    return this.httpRequest.request({
      method: "POST",
      url: "/scored_recommend",
      query: {
        recommendation_id: recommendationId
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`
      }
    });
  }

  /**
   * 推薦結果を返します。
   * @returns RecommendationResult Successful Response
   * @throws ApiError
   */
  public debugGetRecommendation({
    recommendationId,
    requestBody
  }: {
    recommendationId?: string | null;
    requestBody?: RecommenderQueryParams | null;
  }): CancelablePromise<RecommendationResult> {
    return this.httpRequest.request({
      method: "POST",
      url: "/debug_recommend",
      query: {
        recommendation_id: recommendationId
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`
      }
    });
  }

  /**
   * Experimental Get Similar Users
   * @returns string Successful Response
   * @throws ApiError
   */
  public experimentalGetSimilarUsers({
    userId,
    size
  }: {
    userId: string;
    size?: number | null;
  }): CancelablePromise<Array<string>> {
    return this.httpRequest.request({
      method: "GET",
      url: "/users/{user_id}/similar",
      path: {
        user_id: userId
      },
      query: {
        size: size
      },
      errors: {
        422: `Validation Error`
      }
    });
  }

  /**
   * 設定キャッシュをクリアします（デバッグ用）
   * 設定キャッシュをクリアするデバッグエンドポイント
   * @returns any Successful Response
   * @throws ApiError
   */
  public clearConfigCache(): CancelablePromise<any> {
    return this.httpRequest.request({
      method: "POST",
      url: "/debug/clear_config_cache"
    });
  }
}
